import React, { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";

import type { PromoterDisplayMode, TicketTemplateConfig, TicketTemplateId } from "../../types/rsvp";
import { DEFAULT_TICKET_TEMPLATE_CONFIG } from "../../types/rsvp";
import { TicketTemplateCarousel } from "./TicketTemplateCarousel";
import "./ticketTemplate.css";

type PreviewMeta = {
  event_name: string;
  event_date: string;
  city: string;
  artist?: string | null;
  link_kind?: "rsvp" | "guest_list";
  banner_url?: string | null;
  lineup?: { name: string; role?: string | null }[];
};

type Props = {
  open: boolean;
  onClose: () => void;
  eventId: string;
  initialTemplateId: TicketTemplateId;
  initialConfig: TicketTemplateConfig;
  previewMeta: PreviewMeta;
  initialPreviewKind?: "rsvp" | "guest_list";
  previewPromoters?: { id: string; name: string }[];
  onSave: (payload: {
    ticket_template_id: TicketTemplateId;
    template_config: TicketTemplateConfig;
  }) => Promise<void>;
};

export const TicketTemplateEditorModal: React.FC<Props> = ({
  open,
  onClose,
  initialTemplateId,
  initialConfig,
  previewMeta,
  initialPreviewKind = "rsvp",
  previewPromoters = [],
  onSave,
}) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const [templateId, setTemplateId] = useState<TicketTemplateId>(initialTemplateId);
  const [config, setConfig] = useState<TicketTemplateConfig>(initialConfig);
  const [saving, setSaving] = useState(false);

  const previewKind = initialPreviewKind;

  useEffect(() => {
    if (!open) return;
    setTemplateId(initialTemplateId);
    setConfig(initialConfig);
  }, [open, initialTemplateId, initialConfig]);

  const previewMetaWithKind = useMemo(
    () => ({
      ...previewMeta,
      link_kind: previewKind,
      banner_url: config.banner_url ?? previewMeta.banner_url ?? null,
    }),
    [previewMeta, previewKind, config.banner_url],
  );

  if (!open) return null;

  const handleBannerFile = (file: File) => {
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Banner must be under 2 MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === "string" ? reader.result : null;
      setConfig((c) => ({ ...c, banner_url: result, show_banner: true }));
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({ ticket_template_id: templateId, template_config: config });
      toast.success("Ticket template saved");
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not save template");
    } finally {
      setSaving(false);
    }
  };

  const displayModeLabel = (mode: PromoterDisplayMode) =>
    mode === "bold" ? "Bold" : mode === "collapsible" ? "Collapsible" : "Hidden";

  const cyclePromoterDisplay = (mode: PromoterDisplayMode): PromoterDisplayMode => {
    const cycle: PromoterDisplayMode[] = ["hidden", "collapsible", "bold"];
    return cycle[(cycle.indexOf(mode) + 1) % cycle.length];
  };

  return (
    <div className="ticket-template-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="ticket-template-modal ticket-template-modal--wide"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ticket-template-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ticket-template-modal-head">
          <h2 id="ticket-template-modal-title" className="ticket-template-modal-title">
            Templates
          </h2>
          <button type="button" className="eventos-nav-btn" onClick={onClose} disabled={saving}>
            Close
          </button>
        </div>

        <div className="ticket-template-modal-grid ticket-template-modal-grid--wireframe">
          <div className="ticket-template-modal-preview">
            <TicketTemplateCarousel
              templateId={templateId}
              config={config}
              meta={previewMetaWithKind}
              previewPromoters={previewPromoters}
              onSelect={setTemplateId}
            />
          </div>

          <div className="ticket-template-modal-controls">
            <div className="eventos-label">Banner (16:9)</div>
            <div
              className={`ticket-template-banner-upload${
                config.banner_url && config.show_banner ? " ticket-template-banner-upload--has-image" : ""
              }`}
            >
              {config.banner_url && config.show_banner ? (
                <img src={config.banner_url} alt="" className="ticket-template-banner-upload__preview" />
              ) : (
                <div className="ticket-template-banner-upload__placeholder" aria-hidden="true" />
              )}
              <button
                type="button"
                className="eventos-nav-btn ticket-template-banner-upload__btn"
                onClick={() => fileRef.current?.click()}
              >
                Upload
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="visually-hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = "";
                  if (file) handleBannerFile(file);
                }}
              />
            </div>

            <button
              type="button"
              className={`ticket-template-toggle-row${config.lineup_display !== "hidden" ? " is-on" : ""}`}
              onClick={() => setConfig((c) => ({ ...c, lineup_display: cyclePromoterDisplay(c.lineup_display) }))}
            >
              <span>Lineup</span>
              <span className="ticket-template-toggle-row__state">{displayModeLabel(config.lineup_display)}</span>
            </button>

            <button
              type="button"
              className={`ticket-template-toggle-row${config.promoter_display !== "hidden" ? " is-on" : ""}`}
              onClick={() =>
                setConfig((c) => ({ ...c, promoter_display: cyclePromoterDisplay(c.promoter_display) }))
              }
            >
              <span>Promoter</span>
              <span className="ticket-template-toggle-row__state">{displayModeLabel(config.promoter_display)}</span>
            </button>

            <label className="eventos-check">
              <input
                type="checkbox"
                checked={config.show_banner}
                onChange={(e) => setConfig((c) => ({ ...c, show_banner: e.target.checked }))}
              />
              <span>Show banner on ticket</span>
            </label>
          </div>
        </div>

        <div className="ticket-template-modal-actions">
          <button
            type="button"
            className="eventos-nav-btn"
            onClick={() =>
              setConfig({
                ...DEFAULT_TICKET_TEMPLATE_CONFIG,
                banner_url: config.banner_url ?? null,
              })
            }
          >
            Reset toggles
          </button>
          <button type="button" className="eventos-nav-btn" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="eventos-primary-btn" onClick={() => void handleSave()} disabled={saving}>
            {saving ? "Saving…" : "Save template"}
          </button>
        </div>
      </div>
    </div>
  );
};
